/**
 * Where visitors come from (first touch, and the last non-direct touch), so the backend can store it with the user and
 * with every installation start, and the installation funnel in Grafana can be split by channel and landing page.
 *
 * Kept in a cookie on .combidesk.com (not in localStorage, which is per host), so it survives the redirects between
 * combidesk.com and nl.combidesk.com and also reaches the backend on OAuth redirects. The frontend sends it along to
 * the backend in the X-Cd-Attribution header (see attribution.interceptor.ts); the backend's AttributionParser reads it.
 */

export const ATTRIBUTION_COOKIE_NAME = "cd_attr";
export const ATTRIBUTION_HEADER = "X-Cd-Attribution";
const GA_COOKIE_NAME = "_ga";
const MAX_AGE_SECONDS = 180 * 24 * 60 * 60;
const MAX_FIELD_LENGTH = 200;
const OWN_HOST = /(^|\.)combidesk\.(com|nl)$/i;

/** Short keys to keep the cookie small: s/m/c/t/n = utm_source/medium/campaign/term/content, g = gclid,
 *  r = external referrer, p = landing path, h = landing host, d = ISO date. */
export interface AttributionTouch {
  s?: string;
  m?: string;
  c?: string;
  t?: string;
  n?: string;
  g?: string;
  r?: string;
  p?: string;
  h?: string;
  d: string;
}

export interface Attribution {
  v: 1;
  /** the first visit we know of, also when that was a direct visit */
  f: AttributionTouch;
  /** the last visit that wasn't direct */
  l?: AttributionTouch;
  /** the Google Analytics client id (only in the header, never stored in our cookie) */
  ga?: string;
}

type LocationLike = Pick<Location, "search" | "pathname" | "hostname">;

function clip(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed.substring(0, MAX_FIELD_LENGTH) : undefined;
}

/** The referrer, if it's another website: without query string (that may hold personal data), and never our own hosts. */
export function externalReferrer(referrer: string): string | undefined {
  if (!referrer) {
    return undefined;
  }
  try {
    const url = new URL(referrer);
    if (!/^https?:$/.test(url.protocol) || OWN_HOST.test(url.hostname)) {
      return undefined;
    }
    return clip(url.origin + url.pathname);
  } catch {
    return undefined;
  }
}

export function touchFromLocation(location: LocationLike, referrer: string, now: Date): AttributionTouch {
  const params = new URLSearchParams(location.search);
  const touch: AttributionTouch = {
    s: clip(params.get("utm_source")),
    m: clip(params.get("utm_medium")),
    c: clip(params.get("utm_campaign")),
    t: clip(params.get("utm_term")),
    n: clip(params.get("utm_content")),
    g: clip(params.get("gclid")),
    r: externalReferrer(referrer),
    p: clip(location.pathname),
    h: clip(location.hostname),
    d: now.toISOString()
  };
  // leave out empty keys, to keep the cookie small
  Object.keys(touch).forEach(key => touch[key as keyof AttributionTouch] === undefined && delete touch[key as keyof AttributionTouch]);
  return touch;
}

export function isNonDirect(touch: AttributionTouch | undefined): boolean {
  return !!(touch?.s || touch?.m || touch?.g || touch?.r);
}

/**
 * @return the attribution to store, or undefined when nothing changes: the first touch is never overwritten,
 * and the last touch only by a visit that isn't direct (like GA4's "last non-direct click").
 */
export function updateAttribution(existing: Attribution | undefined, touch: AttributionTouch): Attribution | undefined {
  if (!existing) {
    return isNonDirect(touch) ? {v: 1, f: touch, l: touch} : {v: 1, f: touch};
  }
  return isNonDirect(touch) ? {...existing, l: touch} : undefined;
}

export function encodeAttribution(attribution: Attribution): string {
  const bytes = new TextEncoder().encode(JSON.stringify(attribution));
  let binary = "";
  bytes.forEach(b => binary += String.fromCharCode(b));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function decodeAttribution(value: string | undefined): Attribution | undefined {
  if (!value) {
    return undefined;
  }
  try {
    const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(base64 + "=".repeat((4 - base64.length % 4) % 4));
    const parsed = JSON.parse(new TextDecoder().decode(Uint8Array.from(binary, c => c.charCodeAt(0))));
    return parsed?.v === 1 && parsed.f ? parsed as Attribution : undefined;
  } catch {
    return undefined;
  }
}

export function readCookie(doc: Document, name: string): string | undefined {
  const match = doc.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? match[1] : undefined;
}

/** The _ga cookie looks like GA1.1.1234567890.1700000000; the client id is the last two parts. */
export function gaClientId(doc: Document): string | undefined {
  const match = readCookie(doc, GA_COOKIE_NAME)?.match(/^GA\d+\.\d+\.(\d+\.\d+)$/);
  return match ? match[1] : undefined;
}

function writeCookie(doc: Document, value: string): void {
  const hostname = doc.location?.hostname ?? "";
  // on .combidesk.com, so it's shared between combidesk.com, nl.combidesk.com and the backend (combo-backend.combidesk.com)
  const domain = /(^|\.)combidesk\.com$/i.test(hostname) ? "; Domain=combidesk.com" : "";
  const secure = doc.location?.protocol === "https:" ? "; Secure" : "";
  doc.cookie = `${ATTRIBUTION_COOKIE_NAME}=${value}; Max-Age=${MAX_AGE_SECONDS}; Path=/${domain}; SameSite=Lax${secure}`;
}

/** Called when the app starts (so once per page load): records this visit if it's the first one, or a non-direct one. */
export function captureAttribution(doc: Document = document, now: Date = new Date()): void {
  try {
    const existing = decodeAttribution(readCookie(doc, ATTRIBUTION_COOKIE_NAME));
    const updated = updateAttribution(existing, touchFromLocation(doc.location, doc.referrer, now));
    if (updated) {
      writeCookie(doc, encodeAttribution(updated));
    }
  } catch (e) {
    // never let attribution break the app
    console.log("captureAttribution failed: " + e);
  }
}

/** The value for the X-Cd-Attribution header: what's in the cookie, plus the GA client id. */
export function attributionHeaderValue(doc: Document = document): string | undefined {
  const attribution = decodeAttribution(readCookie(doc, ATTRIBUTION_COOKIE_NAME));
  const ga = gaClientId(doc);
  if (!attribution && !ga) {
    return undefined;
  }
  return encodeAttribution({...(attribution ?? {v: 1, f: {d: new Date().toISOString()}}), ...(ga ? {ga} : {})});
}
